import { Button, SegmentedControl, Text, Textarea } from "@mantine/core"
import React from "react"
import { useTranslation } from "react-i18next"
import { useNavigate } from "react-router"
import { mutate } from "swr"
import { OrderApi } from "../../apis"
import HttpHandler from "../../utils/http-handler"
import { message } from "../../utils/message"
import { OrderActionCard } from "../order/order-action-card"
import { useSalesActions } from "../order/order-actions"
import { ReasonTooltip } from "../ui"

/** Modal width of the approve / decline dialogs (Ord_Actions: 440, with the amount). */
export const PURCHASE_CONFIRM_MODAL_SIZE = 440

const refreshOrderBadges = () => {
  mutate("swr.order.count.customer")
  mutate("swr.order.count.sales")
}

type Props = {
  action?: any
  setActions?: any
  constant?: "confirm" | "reject" | string | null
  reload?: any
}

/**
 * Батлах | Татгалзах for a PO in one dialog (Ord_SalesModals 2.4.2). The row action picks the starting
 * side; a decline needs a reason, which the BE keeps on the order. Each decision needs its own
 * permission (BE order/respond.ts).
 */
export function PurchaseSalesConfirmForm({ action, setActions, constant, reload }: Props) {
  const { t } = useTranslation(["order", "supplierSales", "common"])
  const navigate = useNavigate()
  const [loading, setLoading] = React.useState<boolean>(false)
  // order-form (create/detail screen) passes "detail-confirm" / "detail-reject" and expects a return to the list.
  const [approve, setApprove] = React.useState(constant === "confirm" || constant === "detail-confirm")
  const [reason, setReason] = React.useState("")
  const [reasonError, setReasonError] = React.useState(false)
  const leaveAfter = constant === "detail-confirm" || constant === "detail-reject"
  const { permissionState } = useSalesActions()
  const perm = permissionState(approve ? "approve" : "decline")

  const close = () => setActions([])

  const onSubmit = async () => {
    if (!approve && !reason.trim()) {
      setReasonError(true)
      return
    }
    setLoading(true)
    try {
      await OrderApi.respond(action?.id, approve ? { approve } : { approve, reason: reason.trim() })
      refreshOrderBadges()
      message.success(approve ? t("order:purchaseSales.orderApprovedSuccess") : t("order:purchaseSales.orderRejectedSuccess"))
      close()
      if (leaveAfter) navigate("/supplier-purchase")
      else reload?.()
    } catch (err) {
      message.error((err as HttpHandler)?.message!)
    } finally {
      setLoading(false)
    }
  }

  return (
    <OrderActionCard
      title={t("supplierSales:purchase.respondTitle")}
      onClose={close}
      loading={loading}
      footer={
        <>
          <Button variant="default" onClick={close}>{t("common:actions.dismiss")}</Button>
          <ReasonTooltip reason={perm.reason}>
            <Button color={approve ? "dark" : "gray"} variant={approve ? "filled" : "light"} onClick={onSubmit}>
              {approve ? t("supplierSales:purchase.approve") : t("order:purchaseSales.reject")}
            </Button>
          </ReasonTooltip>
        </>
      }
    >
      <SegmentedControl
        w="fit-content"
        color="dark"
        value={approve ? "approve" : "decline"}
        onChange={(v) => setApprove(v === "approve")}
        data={[
          { value: "approve", label: t("supplierSales:purchase.approve") },
          { value: "decline", label: t("order:purchaseSales.reject") },
        ]}
      />
      <Text fz={14}>
        {approve
          ? t("supplierSales:purchase.approveBody", { code: action?.purchaseCode || "-" })
          : t("supplierSales:purchase.declineBody", { code: action?.purchaseCode || "-" })}
      </Text>
      {!approve && (
        <Textarea
          label={t("supplierSales:purchase.declineReason")}
          placeholder="-"
          required
          minRows={3}
          autosize
          maxLength={500}
          value={reason}
          onChange={(e) => {
            setReason(e.currentTarget.value)
            if (reasonError) setReasonError(false)
          }}
          error={reasonError ? t("supplierSales:purchase.declineReasonRequired") : undefined}
        />
      )}
    </OrderActionCard>
  )
}
